// "Talk to HR": an employee hands a question to a person. The request carries who they are (the
// only place Ask HR attaches identity to what someone asked), a category, an optional note, and,
// if they choose, the conversation it came from. HR is emailed and works it in the Requests queue.
// The employee can follow it in Ask HR: its status, and any comment HR addresses to them. HR's own
// notes stay with HR. Most answers go by email, which Ask HR doesn't see.

import { BadRequestError } from "./sources.js";

export const CATEGORIES = {
  leave: "Leave or time off",
  pay: "Pay or benefits",
  policy: "A policy question",
  concern: "A workplace concern",
  other: "Something else",
} as const;

export type Category = keyof typeof CATEGORIES;
export type EscalationStatus = "new" | "in_progress" | "resolved";
export type Channel = "web" | "slack";

export const STATUS_LABELS: Record<EscalationStatus, string> = {
  new: "New",
  in_progress: "In progress",
  resolved: "Resolved",
};

export interface ExcerptTurn {
  role: "user" | "assistant";
  text: string;
}

export interface EscalationEvent {
  at: string;
  by: string;
  status?: EscalationStatus;
  note?: string;
  // "employee": the note is a comment the requester sees. Absent: a note for HR only (every note
  // written before comments existed is one).
  visibility?: "employee";
}

export interface Escalation {
  id: string;
  status: EscalationStatus;
  category: Category;
  note: string;
  channel: Channel;
  requester: { oid: string; name: string; email: string };
  location: string; // "California", "Another US state", "Canada", or "Not set"
  conversationId?: string;
  excerpt: ExcerptTurn[];
  createdAt: string;
  updatedAt: string;
  history: EscalationEvent[];
  emailed: boolean;
  // When the employee removed it from their list in Ask HR. HR keeps the request and sees this.
  hiddenAt?: string;
}

export interface EscalationInput {
  category: Category;
  note: string;
  conversationId?: string;
  includeConversation: boolean;
}

// Telling an employee HR commented on their request: by email and in Slack, with a link and not
// the comment itself (it stays in Ask HR, where only they and HR can see it).
export interface CommentNotice {
  kind: "notify_comment";
  email: string;
  requestId: string;
  category: Category;
}

export function commentNoticeText(category: Category, url: string): { subject: string; text: string; html: string } {
  const about = CATEGORIES[category].toLowerCase();
  const text = `HR added a comment to your request about ${about}. Read it in Ask HR: ${url}`;
  const esc = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
  return {
    subject: "HR commented on your request",
    text: `${text}\n\nReply in Ask HR, or to this email, to reach HR.`,
    html: `<p>HR added a comment to your request about ${esc(about)}.</p><p><a href="${esc(url)}">Read it in Ask HR</a></p><p style="color:#666;font-size:12px">Reply to this email to reach HR.</p>`,
  };
}

// What the requester sees of their own request. Never HR's notes, never other requests.
export interface EmployeeRequest {
  id: string;
  status: EscalationStatus;
  category: Category;
  createdAt: string;
  updatedAt: string;
  summary: string; // the start of their note, for the list and its search
  comments: number;
  lastCommentAt?: string;
}

export interface EmployeeTimelineEntry {
  at: string;
  status?: EscalationStatus;
  comment?: { by: string; text: string };
}

export interface EmployeeRequestDetail extends EmployeeRequest {
  note: string;
  channel: Channel;
  conversationId?: string;
  excerpt: ExcerptTurn[];
  timeline: EmployeeTimelineEntry[];
}

const isComment = (h: EscalationEvent) => h.visibility === "employee" && Boolean(h.note);

export function forEmployee(e: Escalation): EmployeeRequest {
  const comments = e.history.filter(isComment);
  const last = comments.at(-1);
  return {
    id: e.id,
    status: e.status,
    category: e.category,
    createdAt: e.createdAt,
    updatedAt: e.updatedAt,
    summary: e.note.length > 140 ? `${e.note.slice(0, 140)}…` : e.note,
    comments: comments.length,
    ...(last ? { lastCommentAt: last.at } : {}),
  };
}

export function forEmployeeDetail(e: Escalation): EmployeeRequestDetail {
  const timeline: EmployeeTimelineEntry[] = [];
  for (const h of e.history) {
    const comment = isComment(h) && h.note ? { by: h.by, text: h.note } : undefined;
    // An HR-only note that came with a status change still shows the change, without the note.
    if (!comment && !h.status) continue;
    timeline.push({ at: h.at, ...(h.status ? { status: h.status } : {}), ...(comment ? { comment } : {}) });
  }
  return {
    ...forEmployee(e),
    note: e.note,
    channel: e.channel,
    ...(e.conversationId ? { conversationId: e.conversationId } : {}),
    excerpt: e.excerpt,
    timeline,
  };
}

export const MAX_NOTE_CHARS = 2000;
export const MAX_EXCERPT_TURNS = 12;

export function isCategory(v: unknown): v is Category {
  return typeof v === "string" && Object.hasOwn(CATEGORIES, v);
}

export function isStatus(v: unknown): v is EscalationStatus {
  return v === "new" || v === "in_progress" || v === "resolved";
}

export function parseEscalationInput(body: unknown): EscalationInput {
  const b = (typeof body === "object" && body !== null ? body : {}) as Record<string, unknown>;
  if (!isCategory(b.category)) throw new BadRequestError("Choose what this is about.");
  const note = typeof b.note === "string" ? b.note.trim() : "";
  if (note.length > MAX_NOTE_CHARS) throw new BadRequestError(`Notes can be up to ${MAX_NOTE_CHARS} characters.`);
  const conversationId = typeof b.conversationId === "string" && b.conversationId ? b.conversationId : undefined;
  const includeConversation = b.includeConversation !== false && Boolean(conversationId);
  if (!note && !includeConversation) throw new BadRequestError("Add a note, or include the conversation, so HR knows what you need.");
  return { category: b.category, note, ...(conversationId ? { conversationId } : {}), includeConversation };
}

// ---- the email to HR (plain text and a simple HTML version; every value escaped)

const esc = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

export function escalationEmail(e: Escalation, appUrl: string): { subject: string; text: string; html: string } {
  const link = `${appUrl}/admin/requests/${e.id}`;
  const subject = `Ask HR: ${CATEGORIES[e.category]} from ${e.requester.name}`;
  const lines = [
    `${e.requester.name} (${e.requester.email}) asked to talk to HR${e.channel === "slack" ? " from Slack" : ""}.`,
    "",
    `About: ${CATEGORIES[e.category]}`,
    `Works in: ${e.location}`,
    ...(e.note ? ["", "Their note:", e.note] : []),
    ...(e.excerpt.length
      ? ["", "The conversation with Ask HR:", ...e.excerpt.map((t) => `${t.role === "user" ? e.requester.name : "Ask HR"}: ${t.text}`)]
      : []),
    "",
    `Reply to this email to answer them directly. Track it in the HR queue: ${link}`,
  ];
  const html = [
    `<p><strong>${esc(e.requester.name)}</strong> (<a href="mailto:${esc(e.requester.email)}">${esc(e.requester.email)}</a>) asked to talk to HR${e.channel === "slack" ? " from Slack" : ""}.</p>`,
    `<p>About: <strong>${esc(CATEGORIES[e.category])}</strong><br>Works in: ${esc(e.location)}</p>`,
    e.note ? `<p>Their note:</p><blockquote style="white-space:pre-wrap">${esc(e.note)}</blockquote>` : "",
    e.excerpt.length
      ? `<p>The conversation with Ask HR:</p>${e.excerpt
          .map((t) => `<p style="white-space:pre-wrap"><strong>${t.role === "user" ? esc(e.requester.name) : "Ask HR"}:</strong> ${esc(t.text)}</p>`)
          .join("")}`
      : "",
    `<p>Reply to this email to answer them directly. <a href="${esc(link)}">Track it in the HR queue</a>.</p>`,
  ].join("\n");
  return { subject, text: lines.join("\n"), html };
}
